/* eslint-disable @typescript-eslint/ban-types */
import * as Yup from 'yup';

import isValidDate from '~/pages/utils/isValidDate';

import {
  FieldVariableType,
  ProcessForm,
  ProcessFormField,
} from '../parser/types';
import { createCamundaVariable } from '../variable';
import { AnySchema, FormValidationObject, Schema, Validations } from './types';

export function createYupValidationSchemaFromForm(form: ProcessForm) {
  const yupValidations: FormValidationObject = {};

  form.forEach((field) => {
    const { id } = field;
    const validation = formatFieldYupValidation(field, form);

    yupValidations[id] = validation;
  });

  return Yup.object().shape(yupValidations);
}

function formatFieldYupValidation(
  field: ProcessFormField,
  form: ProcessForm,
): AnySchema {
  const {
    type,
    validations: { dataListName, slList, execList },
  } = field;

  if (dataListName || slList || execList) {
    return formatConditionalCamundaValidation(
      Yup.array(),
      field,
      formatCamundaValidationArray,
      form,
    );
  }

  if (type === FieldVariableType.DATE) {
    return formatConditionalCamundaValidation(
      Yup.string(),
      field,
      formatCamundaValidationDate,
      form,
    );
  }

  if (type === FieldVariableType.LONG) {
    return formatConditionalCamundaValidation(
      Yup.number(),
      field,
      formatCamundaValidationLong,
      form,
    );
  }

  if (type === FieldVariableType.ENUM) {
    return formatConditionalCamundaValidation(
      Yup.object(),
      field,
      formatCamundaValidationObject,
      form,
    );
  }

  return formatConditionalCamundaValidation(
    Yup.string(),
    field,
    formatCamundaValidationString,
    form,
  );
}

function formatCamundaValidationObject(
  schema: Yup.ObjectSchema<object>,
  validations: Validations,
): Yup.ObjectSchema<object> {
  const { isRequired } = validations;

  return schema
    .typeError('Campo obrigatório!')
    .test('camundaValidationsObject', 'Preenchimento inválido', function test(
      value,
    ) {
      const { path, createError } = this;

      if (isRequired && !value) {
        return createError({
          path,
          message: 'Campo obrigatório!',
        });
      }

      return true;
    });
}

function formatCamundaValidationArray(
  schema: Yup.ArraySchema<unknown>,
  validations: Validations,
): Yup.ArraySchema<unknown> {
  const { isRequired } = validations;

  return schema
    .typeError('Campo obrigatório!')
    .test('camundaValidationsObject', 'Preenchimento inválido', function test(
      value,
    ) {
      const { path, createError } = this;

      if (isRequired && !value) {
        return createError({
          path,
          message: 'Campo obrigatório!',
        });
      }

      return true;
    });
}

function formatCamundaValidationString(
  schema: Yup.StringSchema<string>,
  validations: Validations,
): Yup.StringSchema<string> {
  const { isRequired, minimumLength, maximumLength, pattern } = validations;

  return schema.test(
    'camundaValidationsString',
    'Preenchimento inválido',
    function test(value) {
      const { path, createError } = this;

      if (isRequired && !value) {
        return createError({
          path,
          message: 'Campo obrigatório!',
        });
      }

      if (pattern) {
        const RegExpFormat = new RegExp(pattern);
        const formatValidPattern = RegExpFormat.test(String(value));

        if (!formatValidPattern) {
          return createError({
            path,
            message: 'O valor preenchido é inválido.',
          });
        }
      }

      if (minimumLength && value?.length < minimumLength) {
        return createError({
          path,
          message: `O campo precisa ter ao menos ${minimumLength} caracteres`,
        });
      }

      if (maximumLength && value?.length > maximumLength) {
        return createError({
          path,
          message: `O campo pode ter no máximo ${maximumLength} caracteres`,
        });
      }

      return true;
    },
  );
}

function formatCamundaValidationDate(
  schema: Yup.StringSchema<string>,
  validations: Validations,
): Yup.StringSchema<string> {
  const { isRequired } = validations;

  return schema.test(
    'camundaValidationsDate',
    'Preenchimento inválido',
    function test(value) {
      const { path, createError } = this;

      if (!isRequired && !value) return true;

      if (isRequired && !value) {
        return createError({
          path,
          message: 'Campo obrigatório!',
        });
      }

      const valid = isValidDate({
        date: value,
        dateFormat: 'DD/MM/YYYY',
      });

      if (value !== 'Invalid date' && !valid) {
        return createError({
          path,
          message: 'Digite uma data válida!',
        });
      }

      return true;
    },
  );
}

function formatCamundaValidationLong(
  schema: Yup.NumberSchema<number>,
  validations: Validations,
): Yup.NumberSchema<number> {
  const { isRequired, minimumNumber, maximumNumber } = validations;

  return schema
    .typeError('Campo obrigatório!')
    .test('camundaValidationsLong', 'Preenchimento inválido', function test(
      value,
    ) {
      const { path, createError } = this;

      if (isRequired && !value) {
        return createError({
          path,
          message: 'Campo obrigatório!',
        });
      }

      if (minimumNumber && value < minimumNumber) {
        return createError({
          path,
          message: `O valor precisa ser maior ou igual a ${minimumNumber}`,
        });
      }

      if (maximumNumber && value > maximumNumber) {
        return createError({
          path,
          message: `O valor precisa ser menor ou igual a ${maximumNumber}`,
        });
      }

      return true;
    });
}

function formatConditionalCamundaValidation<T extends AnySchema>(
  schema: T,
  field: ProcessFormField,
  action: (schema: T, validations: Validations) => Schema<T>,
  form: ProcessForm,
): Schema<T> {
  const { validations } = field;
  const { conditionalInput, conditionalAction, conditionalValue } = validations;

  if (conditionalInput && conditionalAction) {
    return schema.nullable().when(conditionalInput, {
      is: (value: unknown) => {
        const refInput = form.find((field) => field.id === conditionalInput);
        const convertedValue = refInput
          ? createCamundaVariable(refInput, value).value
          : null;
        return (
          (conditionalAction === 'show' &&
            convertedValue === conditionalValue) ||
          (conditionalAction === 'filter' && convertedValue)
        );
      },
      then: (schema: T) => action(schema, validations),
    }) as Schema<T>;
  }
  return action(schema, validations).nullable() as Schema<T>;
}
